/**
 * analytics-utm.ts — captura e persistência de UTM parameters.
 *
 * Estratégia (P4):
 * - First-touch: salvo em localStorage na primeira visita com UTMs.
 *   Persiste entre sessões. Aplicado como user property via setUserPropertyOnce.
 * - Last-touch: salvo em sessionStorage a cada visita com UTMs.
 *   Super property de sessão — sobrescrito a cada nova entrada.
 */

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmParam = (typeof UTM_PARAMS)[number];
type UtmMap = Partial<Record<UtmParam, string>>;

const FIRST_TOUCH_PREFIX = 'ph_utm_first_';
const LAST_TOUCH_PREFIX = 'ph_utm_last_';
const CAPTURED_FLAG = 'ph_utm_captured';

/**
 * Faz parse dos UTM params da URL atual.
 * Retorna objeto vazio se nenhum UTM presente.
 */
function parseUtmsFromSearch(search: string): UtmMap {
  const params = new URLSearchParams(search);
  const result: UtmMap = {};

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Lê UTMs first-touch do localStorage.
 */
function readFirstTouchFromStorage(): UtmMap {
  const result: UtmMap = {};

  for (const key of UTM_PARAMS) {
    const stored = localStorage.getItem(`${FIRST_TOUCH_PREFIX}${key}`);
    if (stored) {
      result[key] = stored;
    }
  }

  return result;
}

/**
 * Salva UTMs first-touch no localStorage (somente se ainda não foi salvo).
 */
function saveFirstTouch(utms: UtmMap): void {
  for (const key of UTM_PARAMS) {
    const value = utms[key];
    if (value && !localStorage.getItem(`${FIRST_TOUCH_PREFIX}${key}`)) {
      localStorage.setItem(`${FIRST_TOUCH_PREFIX}${key}`, value);
    }
  }
}

/**
 * Salva UTMs last-touch no sessionStorage (sobrescreve sempre).
 */
function saveLastTouch(utms: UtmMap): void {
  for (const key of UTM_PARAMS) {
    const value = utms[key];
    if (value) {
      sessionStorage.setItem(`${LAST_TOUCH_PREFIX}${key}`, value);
    }
  }
}

/**
 * Captura UTMs da URL atual e persiste first/last touch.
 * Idempotente — pode ser chamado múltiplas vezes por sessão.
 *
 * @returns { firstTouch, lastTouch } — mapas de UTMs capturados.
 */
export function captureUtms(): { firstTouch: UtmMap; lastTouch: UtmMap } {
  if (typeof window === 'undefined') {
    return { firstTouch: {}, lastTouch: {} };
  }

  const currentUtms = parseUtmsFromSearch(window.location.search);
  const hasUtms = Object.keys(currentUtms).length > 0;

  if (hasUtms) {
    // First-touch: salva somente se chaves ainda não existem no localStorage.
    saveFirstTouch(currentUtms);
    // Last-touch: sempre sobrescreve na sessionStorage.
    saveLastTouch(currentUtms);
    // Marca que já capturou nesta sessão.
    sessionStorage.setItem(CAPTURED_FLAG, '1');
  }

  // Lê first-touch do storage (pode ter sido salvo em visita anterior).
  const firstTouch = readFirstTouchFromStorage();

  return { firstTouch, lastTouch: currentUtms };
}

/**
 * Aplica UTMs first-touch como user properties no PostHog.
 * Usar setUserPropertyOnce para não sobrescrever o first-touch original.
 * Chamar após analytics.identify() no AuthContext.
 *
 * @param setUserPropertyOnce - função do analytics wrapper
 */
export function applyUtmUserProperties(
  setUserPropertyOnce: (key: string, value: unknown) => void
): void {
  const { firstTouch } = captureUtms();

  for (const key of UTM_PARAMS) {
    const value = firstTouch[key];
    if (value) {
      setUserPropertyOnce(`utm_first_${key.replace('utm_', '')}`, value);
    }
  }
}

/**
 * Retorna UTMs last-touch da sessionStorage para registrar como super property.
 */
export function getLastTouchUtms(): UtmMap {
  if (typeof window === 'undefined') return {};

  const result: UtmMap = {};

  for (const key of UTM_PARAMS) {
    const stored = sessionStorage.getItem(`${LAST_TOUCH_PREFIX}${key}`);
    if (stored) {
      result[key] = stored;
    }
  }

  return result;
}
